<template>
  <div>
    <div v-if="index >= 1" class="flex justify-between">
      <h5 class="text-[19px]/[22px] sm:text-[27px]/[31px] font-semibold mt-[17px] mb-[13px]">
        Subscription #{{ index + 1 }}
      </h5>
      <button @click.prevent="removeSubscription" class="text-lg font-bold text-vivid-purple">
        Remove
      </button>
    </div>
    <DeliveryZipCode
        :zipCode="subscription.zipCode"
        :errors="errors"
        @update:modelValue="updateZipCode"
        :index="index"
        :checkZip="dataZip?.status"
        @updateZipCode="updateZipCode"
    />
    <span v-if="dataZip?.err && zipVal.length <= 5" class="text-red-500 text-xs"
    >Please contact our office 718.517.8310 to set up a subscription for this ZIP code</span
    >
    <div
        :class="dataZip?.status ? 'opacity-100' : 'opacity-50 pointer-events-none'"
        class="transition-all duration-300 ease-linear"
    >
      <SubscriptionPreferance
          :choosePlan="choosePlan"
          :allIssues="allIssues"
          :updateChoosePlan="updateChoosePlan"
          :dataZip="dataZip?.data"
          :zoneInfo="zoneInfo"
          :index="index"
      />
      <div v-if="index === 0">
        <YourInformation :emailEmptyShowChecker="emailEmptyShowChecker" :errors="errors" :onEmailBlur="onEmailBlur"/>
      </div>
      <div v-if="index === 0" class="pt-6 pb-[31px] border-b border-b-light-gray">
        <Field
            v-slot="{ field }"
            name="signup"
            type="checkbox"
            :value="true"
        >
          <Checkbox label="Sign up for Vinderkind emails"
                    :fieldAttrs="field"
                    v-model="signUpEmails"
                    :uncheckValue="false"
                    uncheckedClass="text-black"
                    :value="true"/>
        </Field>
      </div>
      <DeliverInformation :deliveryInfo="deliveryInfo" :errors="errors"/>
      <button
          v-if="false"
          @click.prevent="addSubscription"
          class="text-vivid-purple text-base sm:text-lg font-bold font-raleway flex items-center gap-[9.5px] mt-[17px]"
      >
        <IconPlus class="[&_path]:fill-vivid-purple"/>
        Add Another Subscription
      </button>
    </div>
  </div>
</template>
<script lang="ts">
import {defineComponent, onMounted, ref, type PropType} from 'vue'
import DeliveryZipCode from '@components/sign-up/DeliveryZipCode.vue'
import SubscriptionPreferance from '@components/sign-up/SubscriptionPreferance.vue'
import YourInformation from '@components/sign-up/YourInformation.vue'
import DeliverInformation from '@components/sign-up/DeliverInformation.vue'
import IconPlus from '@components/icons/IconPlus.vue'
import {useIssuesStore} from '@stores/issues'
import {useTotalPaymentStore} from '@stores/totalPayment'
import {useSignUpCartsStore} from '@stores/sign-up-cart'
import {emailValidationWithEmpty, required} from '@utills/helpers/validation'
import {emailValidation} from '@utills/helpers/validation'
import {fetchData} from '@api/query'
import {Field} from 'vee-validate'
import type {IPlan} from '@utills/types'
import Checkbox from "@components/reusable/Checkbox.vue";

export default defineComponent({
  components: {
    Checkbox,
    DeliveryZipCode,
    SubscriptionPreferance,
    YourInformation,
    DeliverInformation,
    IconPlus,
    Field
  },
  props: {
    emailEmptyShowChecker: {
      type: Boolean,
      required: true,
      default:false
    },
    errors: {
      type: Object,
      required: true
    },
    addSubscription: {
      type: Function as PropType<(payload?: MouseEvent) => void>,
      required: true
    },
    subscription: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    allSubscription: {
      type: Object,
      required: true
    },
    onEmailBlur: {
      type: Function,
      required: false,
      default: null
    }
  },
  data(props) {
    return {
      zipVal: '',
      zoneInfo: {} as any
    }
  },
  computed: {
    deliveryInfo() {
      const countryCode = this.zoneInfo?.country_code;
      const baseFields: any[] = [
        {
          placeholder: 'Name',
          field: 'delivery_name' + '-' + this.index,
        },
        {
          placeholder: 'Address',
          field: 'address_line_1' + '-' + this.index,
        },
        {
          placeholder: 'Address Line 2',
          field: 'address_line_2' + '-' + this.index,
        },
        {
          placeholder: 'City',
          field: 'city' + '-' + this.index,
        }
      ];

      // Add country-specific fields
      if (countryCode === 'GB') {
        // UK Address Format
        baseFields.push({
          placeholder: 'County (Optional)',
          field: 'state' + '-' + this.index,
          required: false
        });
        baseFields.push({
          placeholder: 'Postcode',
          field: 'zip' + '-' + this.index,
          handler: false
        });
      } else if (countryCode === 'CA') {
        // Canadian Address Format
        baseFields.push({
          placeholder: 'Province',
          field: 'state' + '-' + this.index,
        });
        baseFields.push({
          placeholder: 'Postal Code',
          field: 'zip' + '-' + this.index,
          handler: false
        });
      } else {
        // US Address Format (default)
        baseFields.push({
          placeholder: 'State',
          field: 'state' + '-' + this.index,
        });
        baseFields.push({
          placeholder: 'ZIP Code',
          field: 'zip' + '-' + this.index,
          handler: false
        });
      }

      return baseFields;
    }
  },
  methods: {
    removeSubscription() {
      this.$props.allSubscription.splice(this.$props.index, 1)
      this.deleteZip(this.index)
    }
  },
  setup(props, { emit }) {
    let dataZip = ref()
    let choosePlan = ref(0)
    const signUpEmails = ref()
    const zipVal = ref('')
    const zoneInfo = ref({})

    const IssuesStore = useIssuesStore()
    const {clearCart} = useSignUpCartsStore()
    const allIssues = ref([])

    onMounted(async () => {
      clearCart()
      await IssuesStore.fetchIssues()
      allIssues.value = IssuesStore.allIssues
    })

    const {totalPayment, handleZipsPayment, deleteZip} = useTotalPaymentStore()

    const updateChoosePlan = (planId: number, plan: Object) => {
      deleteZip(planId)
      handleZipsPayment({
        id: planId,
        ...plan
      })

      if(choosePlan.value === null){
        choosePlan.value = dataZip?.value?.data.find((plan: IPlan) => plan.default)?.id
      }else{
        choosePlan.value = dataZip?.value?.data.find((plan: IPlan) => plan.id == planId)?.id
      }
    }

    const updateZipCode = (value: string) => {
      emit('update:subscription', value)
      zipVal.value = value
      const id = props.index

      // Check if it's a valid postal code format (US, UK, or Canadian)
      const isValidFormat = /^\d{5}$/.test(value) || // US ZIP
                           /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value) || // UK postcode
                           /^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/i.test(value); // Canadian postal code

      if (isValidFormat) {
        fetchData(`zip/${value}`)
            .then((res) => {
              const isErrorResponse = (response: any): response is { err: string } => {
                return response && typeof response.err === 'string';
              };

              if (!isErrorResponse(res)) {
                // fetchData returns axios response, so actual data is at res.data
                const apiData = res?.data

                // Extract plans array and zone info
                const plans = apiData?.data || []
                const zoneInfoData = apiData?.zone || {}

                // Set the reactive dataZip reference
                dataZip.value = {
                  data: plans,
                  status: true
                }
                zoneInfo.value = zoneInfoData
                emit('enableSection', true)
                emit('updateZoneInfo', zoneInfoData)

                // Find and select default plan
                const defaultPlan = plans?.find((plan: IPlan) => plan.default)

                if (defaultPlan) {
                  updateChoosePlan(defaultPlan.id, defaultPlan)
                }
              } else {
                dataZip.value = { data: [], err: true, status: false }
                emit('enableSection', false)
                deleteZip(id)
              }
            }).catch((error) => {
              dataZip.value = { data: [], err: true, status: false }
              emit('enableSection', false)
              deleteZip(id)
            })
      } else {
        dataZip.value = { data: [], err: false, status: false }
        emit('enableSection', false)
        deleteZip(id)
      }
    }

    return {
      signUpEmails,
      choosePlan,
      allIssues,
      updateChoosePlan,
      updateZipCode,
      emailValidation,
      required,
      dataZip,
      totalPayment,
      handleZipsPayment,
      deleteZip,
      zipVal,
      zoneInfo,
    }
  }
})
</script>
